import { Link, useLocation, useNavigate } from "react-router-dom";
import { getListeSecteur } from "../../data/params/secteur";
import { getListeStatut } from "../../data/params/statut";
import "./dossiers.css";

// UI imports
import { FolderOpenOutlined } from "@ant-design/icons";
import {
  Button,
  Col,
  Input,
  message,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
} from "antd";
import { Fragment, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import PageLabel from "../../components/common/PageLabel/PageLabel";
import { FormContainer } from "../../components/common/common.components";
import FormLabel from "../../components/common/Form/FormLabel";
import SearchRow from "../../components/common/SearchRow";
import TableTitle from "../../components/common/TableTitle";
import { faEye, faFileExport, faUser } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { HStack } from "@chakra-ui/react";
import * as XLSX from "xlsx";

const DossierList = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // États
  const [filtersItems, setFiltersItems] = useState([]);
  const [statuts, setStatuts] = useState([]);
  const [secteurs, setSecteurs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [paramsRecherche, setParamsRecherche] = useState({
    infosClient: "",
    secteur_id: "",
    statut_id: "",
  });

  const user = useSelector((state) => state.user);
  const isAdmin = user.libelleProfil === "Admin"; // (au cas où, mais non utilisé ici)

  const [clientsDossiers, setClientsDossiers] = useState(
    location?.state?.dossiers || []
  );

  const [tableParams, setTableParams] = useState({
    pagination: { current: 1, pageSize: 10, filtersItems },
  });

  // Charger statuts et secteurs au montage
  useEffect(() => {
    fetchStatuts();
    fetchSecteurs();
  }, []);

  // Si on reçoit des dossiers en props (via location.state), on les injecte dans le tableau
  useEffect(() => {
    if (clientsDossiers.length) {
      setLoading(true);
      setFiltersItems(clientsDossiers);
      setLoading(false);
    }
  }, [clientsDossiers]);

  // --- Fetch des secteurs
  const fetchSecteurs = async () => {
    setLoading(true);
    const res = await getListeSecteur();
    setSecteurs(res?.result || []);
    setLoading(false);
  };

  // --- Fetch des statuts
  const fetchStatuts = async () => {
    setLoading(true);
    const res = await getListeStatut();
    setStatuts(res?.result || []);
    setLoading(false);
  };

  // --- Gestion du changement de table (pagination, tri, filtres)
  const handleTableChange = (pagination, filters, sorter) => {
    setTableParams({ pagination, filters, ...sorter });
    if (pagination.pageSize !== tableParams.pagination?.pageSize) {
      setFiltersItems([]);
    }
  };

  // --- Export en Excel
  const handleExport = () => {
    try {
      const csvData = filtersItems.map((item) => ({
        Sigle: item?.client?.sigle,
        Dénomination: item?.client?.denomination,
        Mission: item?.mission?.mission,
        Année: item?.mission?.annee,
        "Secteur d'activité": item?.client?.SecteurActivite?.libelle,
        Manager: `${item?.nom_manager || ""} ${
          item?.prenom_manager || ""
        }`.trim(),
        "Statut juridique": item?.client?.StatusJuridique?.libelle,
        Statut: !item?.isCloture
          ? item?.isLettreMission === 0 && item?.mission?.etat === 1
            ? "En attente"
            : "En cours"
          : "Clôturé",
      }));

      const worksheet = XLSX.utils.json_to_sheet(csvData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Actes");

      XLSX.writeFile(workbook, "liste_dossiers_lea.xlsx");
      message.success("Export réussi !");
    } catch (error) {
      console.error(error);
      message.error("Erreur lors de l'export");
    }
  };

  // --- Colonnes de la table
  const exercicesUserColumns = [
    {
      title: <TableTitle>#</TableTitle>,
      width: 50,
      render: (_, __, index) => <>{index + 1}</>,
    },
    {
      title: <TableTitle>Exercice</TableTitle>,
      dataIndex: "exercice",
      key: "exercice",
      render: (_, record) => (
        <div className="d-flex align-items-center">
          <div className="symbol symbol-45px me-5">
            <FolderOpenOutlined
              style={{ fontSize: "3rem", color: "rgb(250, 152, 28)" }}
            />
          </div>
          <div className="d-flex justify-content-start flex-column">
            <Link
              className="text-gray-800 text-hover-primary"
              to={`/clients/mission/exploitation/${record?.exercice_id}`}
              state={{ record: record?.client, mission: record?.mission }}
            >
              <span className="text-dark fw-bolder text-hover-primary fs-6">
                {record?.mission_name || ""} - {record?.mission_annee || ""}
              </span>
              <span className="text-muted fw-bold text-muted d-block fs-9 text-uppercase">
                <Tag>{record?.sigle_client}</Tag> | {record?.status_client}
              </span>
            </Link>
          </div>
        </div>
      ),
    },
    {
      title: <TableTitle>Secteur d'activité</TableTitle>,
      dataIndex: "secteur_client",
      key: "secteur_client",
      render: (_, record) => (
        <span className="text-black fw-bold d-block fs-5 text-uppercase">
          {record?.secteur_client}
        </span>
      ),
    },
    {
      title: <TableTitle>Manager</TableTitle>,
      dataIndex: "manager",
      key: "manager",
      render: (_, record) => (
        <Space>
          <div className="fs-2 text-warning pe-2">
            <FontAwesomeIcon icon={faUser} />
          </div>
          <span className="text-dark fw-bolder text-hover-primary d-block fs-5 text-uppercase">
            {record?.nom_manager
              ? `${record?.nom_manager} ${record?.prenom_manager || ""}`
              : ""}
          </span>
        </Space>
      ),
    },
    {
      title: <TableTitle>Statut</TableTitle>,
      dataIndex: "status",
      key: "status",
      render: (_, record) =>
        !record?.isCloture ? (
          record?.isLettreMission === 0 && record?.mission?.etat === 1 ? (
            <Tag color="yellow">En attente</Tag>
          ) : (
            <Tag color="blue">En cours</Tag>
          )
        ) : (
          <Tag color="green">Clôturé</Tag>
        ),
    },
    {
      title: <TableTitle>Actions</TableTitle>,
      dataIndex: "actions",
      align: "right",
      width: 100,
      key: "actions",
      render: (_, record) => (
        <Tooltip title="Consulter le sommaire">
          <Button
            type="primary"
            icon={<FontAwesomeIcon icon={faEye} />}
            onClick={() =>
              navigate(`/clients/mission/exploitation/${record?.exercice_id}`, {
                state: { record: record?.client, mission: record?.mission },
              })
            }
          />
        </Tooltip>
      ),
    },
  ];

  // --- Recherche (filtrage client/mission + secteur + statut)
  const handleSearch = () => {
    setLoading(true);
    setTimeout(() => {
      const filtered = clientsDossiers.filter(
        (dossier) =>
          (dossier?.sigle_client
            ?.toLowerCase()
            .includes(paramsRecherche.infosClient.toLowerCase()) ||
            dossier?.mission_name
              ?.toLowerCase()
              .includes(paramsRecherche.infosClient.toLowerCase())) &&
          (paramsRecherche.secteur_id === "" ||
            dossier?.client?.secteur_id ===
              Number(paramsRecherche.secteur_id)) &&
          (paramsRecherche.statut_id === "" ||
            dossier?.client?.statut_id === Number(paramsRecherche.statut_id))
      );
      setFiltersItems(filtered);
      setLoading(false);
    }, 1000);
  };

  // --- Reset recherche
  const handleReset = () => {
    setLoading(true);
    setParamsRecherche({ infosClient: "", secteur_id: "", statut_id: "" });
    setFiltersItems(clientsDossiers);
    setLoading(false);
  };

  return (
    <Fragment>
      {/* Titre */}
      <HStack justifyContent="space-between" alignItems="center">
        <PageLabel label="Mes dossiers" />
      </HStack>

      {/* Zone de recherche */}
      <SearchRow
        handleSearch={handleSearch}
        handleReset={handleReset}
        loading={loading}
      >
        <Col span={8}>
          <FormLabel label="Mission/Client" />
          <Input
            style={{ width: "100%" }}
            size="large"
            placeholder="Mission ou client"
            value={paramsRecherche.infosClient}
            onChange={(e) =>
              setParamsRecherche({
                ...paramsRecherche,
                infosClient: e.target.value,
              })
            }
          />
        </Col>
        <Col span={8}>
          <FormLabel label="Statut juridique" />
          <Select
            placeholder="Statut juridique"
            size="large"
            style={{ width: "100%" }}
            value={paramsRecherche.statut_id}
            onSelect={(value) =>
              setParamsRecherche({ ...paramsRecherche, statut_id: value })
            }
            options={
              statuts?.map((s) => ({ value: s?.id, label: s?.libelle })) || []
            }
          />
        </Col>
        <Col span={8}>
          <FormLabel label="Secteur d'activité" />
          <Select
            placeholder="Secteur d'activité"
            size="large"
            style={{ width: "100%" }}
            value={paramsRecherche.secteur_id}
            onSelect={(value) =>
              setParamsRecherche({ ...paramsRecherche, secteur_id: value })
            }
            options={
              secteurs?.map((s) => ({ value: s?.id, label: s?.libelle })) || []
            }
          />
        </Col>
      </SearchRow>

      {/* Bouton export */}
      <Space className="d-flex justify-content-end pt-4">
        <Button
          type="primary"
          icon={<FontAwesomeIcon icon={faFileExport} />}
          onClick={handleExport}
        >
          Exporter
        </Button>
      </Space>

      {/* Tableau */}
      <FormContainer>
        <div className="w-100 py-4">
          <div className="table-responsive">
            <Table
              columns={exercicesUserColumns}
              rowKey={(record) => record.id}
              dataSource={filtersItems}
              pagination={tableParams.pagination}
              loading={loading}
              onChange={handleTableChange}
            />
          </div>
        </div>
      </FormContainer>
    </Fragment>
  );
};

export default DossierList;
